import  { createContext, useContext, useState, ReactNode } from 'react';

interface CampaignIdsContextType {
  campaignIds: string[];
  setCampaignIds: (ids: string[]) => void;
  addCampaignId: (id: string) => void;
  removeCampaignId: (id: string) => void;
  clearCampaignIds: () => void;
}

const CampaignIdsContext = createContext<CampaignIdsContextType | undefined>(undefined);

export function CampaignIdsProvider({ children }: { children: ReactNode }) {
  const [campaignIds, setCampaignIds] = useState<string[]>([]);

  const addCampaignId = (id: string) => {
    setCampaignIds(prev => {
      // Only add if the ID doesn't already exist
      if (!prev.includes(id)) {
        return [...prev, id];
      }
      return prev;
    });
  };

  const removeCampaignId = (id: string) => {
    setCampaignIds(prev => prev.filter(campaignId => campaignId !== id));
  };

  const clearCampaignIds = () => {
    setCampaignIds([]);
  };

  // Ensure unique IDs when setting multiple IDs at once
  const setUniqueCampaignIds = (ids: string[]) => {
    setCampaignIds([...new Set(ids)]);
  };

  return (
    <CampaignIdsContext.Provider
      value={{
        campaignIds,
        setCampaignIds: setUniqueCampaignIds,
        addCampaignId,
        removeCampaignId,
        clearCampaignIds,
      }}
    >
      {children}
    </CampaignIdsContext.Provider>
  );
}

export function useCampaignIds() {
  const context = useContext(CampaignIdsContext);
  if (context === undefined) {
    throw new Error('useCampaignIds must be used within a CampaignIdsProvider');
  }
  return context;
} 